// src/onboarding/OnboardingSubSpaces.jsx
// Phase 37 — extracted from src/App.jsx (Plan 37-03).
//
// Scope: per-area sub-spaces screen (ONB-09, Phase 24 Plan 04 — D-01..D-08).
// Renders once per selected garden area between OnboardingAreas and OnboardingPlant.

import { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { ClearableInput } from '../App.jsx';
import OnbFrame from './OnbFrame.jsx';
import OnbPlotPlan from './OnbPlotPlan.jsx';
import { ONB_AREA_PATH_FALLBACK, ONB_AREA_PATHS } from './constants.js';
import { INITIAL_AREAS } from '../data/areas.js';

export default function OnboardingSubSpaces({ areaName, areas, setAreas, onContinue, step = 3, total = 5 }) {
  const area = useMemo(
    () => (areas || []).find(a => a.name.toLowerCase() === areaName.toLowerCase()),
    [areas, areaName]
  );
  const existingSubs = area?.subAreas || [];
  const initialPreset = useMemo(() => {
    const ia = INITIAL_AREAS.find(a => a.name.toLowerCase() === areaName.toLowerCase());
    return ia?.subAreas || [];
  }, [areaName]);

  // D-08: existing first (preserves user customs + their insertion order), then missing presets appended
  const seed = useMemo(() => [
    ...existingSubs,
    ...initialPreset.filter(p => !existingSubs.some(s => s.toLowerCase() === p.toLowerCase())),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [areaName]); // seed only recomputes when area name changes; re-seeding mid-session would desync ticked state

  const [chips, setChips] = useState(seed);
  const [ticked, setTicked] = useState(() => new Set(seed)); // D-02 / D-08 all pre-ticked
  const [customInput, setCustomInput] = useState("");

  const toggle = (name) => {
    setTicked(prev => {
      const n = new Set(prev);
      if (n.has(name)) n.delete(name); else n.add(name);
      return n;
    });
  };

  const addCustom = () => {
    const trimmed = customInput.trim().slice(0, 40);
    if (!trimmed) return;
    if (chips.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
      setCustomInput("");
      return;
    }
    setChips(prev => [...prev, trimmed]);
    setTicked(prev => new Set([...prev, trimmed]));
    setCustomInput("");
  };

  const handleContinue = () => {
    // Flush any pending custom input — the user may have typed a new sub-space
    // and clicked "Done" without first pressing Enter or the + button.
    // Silently losing their typing is the bug we're guarding against here.
    const pending = customInput.trim().slice(0, 40);
    const hasPending = pending && !chips.some(c => c.toLowerCase() === pending.toLowerCase());
    const finalChips = hasPending ? [...chips, pending] : chips;
    const finalTicked = hasPending ? new Set([...ticked, pending]) : ticked;
    const next = finalChips.filter(c => finalTicked.has(c));
    setAreas(prev => (prev || []).map(a =>
      a.id === area?.id ? { ...a, subAreas: next } : a
    ));
    onContinue();
  };

  // D-03 — plot plan highlighting the current area. Full garden view so
  // the user sees where this area sits relative to the others.
  const derivedPlotAreas = useMemo(() => (
    (areas || []).map(a => {
      const geom = ONB_AREA_PATHS[a.name] || ONB_AREA_PATH_FALLBACK;
      return {
        name: a.name, label: a.name,
        path: geom.path, lx: geom.lx, ly: geom.ly,
        color: geom.color, hatch: geom.hatch,
      };
    })
  ), [areas]);

  // Pin placement inside the highlighted area — approximate coords inside
  // each preset shape. Prototype-derived positions.
  const PIN_POSITIONS = {
    "Back Garden":  [{x:240,y:72},{x:285,y:80},{x:320,y:100},{x:265,y:115},{x:300,y:130},{x:235,y:130},{x:330,y:75},{x:250,y:90},{x:310,y:118}],
    "Front Garden": [{x:55,y:70},{x:90,y:85},{x:120,y:80},{x:70,y:95},{x:100,y:70},{x:45,y:85}],
    "Balcony":      [{x:55,y:180},{x:80,y:190},{x:100,y:180}],
    "Allotment":    [{x:250,y:180},{x:280,y:195},{x:310,y:200},{x:255,y:210}],
    "Greenhouse":   [{x:165,y:175},{x:190,y:180},{x:205,y:185}],
  };
  const PIN_COLORS = ["#d98b5f","#7a9865","#a78bca","#c96878","#e8a547","#6b8a55","#d98b5f","#a78bca","#7a9865"];

  // Phase 31.3 Gap 6 — derive pins from the TICKED subset of chips so the
  // plot reacts when chips toggle. The prototype coupled `ticked` to the pin
  // label (tend-design-system/onboarding/screens3-4.jsx:186); Plan 04 Deviation
  // #2 stripped the label to avoid a getByText('Veg Patch') RTL collision at
  // src/App.test.jsx:1867, which severed reactivity. Option B from the debug
  // session (.planning/debug/subspaces-pins-not-reactive.md) restores reactivity
  // via pin COUNT instead of label presence — ticking drops a pin, unticking
  // removes it. The onb-pin-drop keyframe re-fires on each appearance, giving
  // the promised "pins drop when placed" micro-interaction (design-system brief
  // tend-design-system/Onboarding.html:192). Pin label still OMITTED — the RTL
  // test contract at App.test.jsx:1867 is preserved.
  // Pin count capped at pinPositions.length (T-31.3-09 mitigation).
  const pinPositions = PIN_POSITIONS[areaName] || [];
  const tickedChips = chips.filter(c => ticked.has(c));
  const pins = tickedChips.slice(0, pinPositions.length).map((name, i) => ({
    x: pinPositions[i].x,
    y: pinPositions[i].y,
    color: PIN_COLORS[i % PIN_COLORS.length],
    // label intentionally omitted — RTL getByText('Veg Patch') singular matcher
    // at App.test.jsx:1867 would break if re-introduced.
  }));

  return (
    <OnbFrame step={step} total={total} ground="dawn">
      <div className="onb-content">
        <div style={{ height: 14 }}/>
        <p className="onb-eyebrow">Looking closer</p>
        {/* [Rule 3 - Blocking] Heading kept as a single text node so
            screen.getByText(/What's inside your Back Garden/i) matches —
            using <em>{areaName}</em> would split the text across elements
            and break 4 existing sub-space tests. Visual emphasis restored
            via CSS colour on .onb-heading if needed. */}
        <h1 className="onb-heading">What's inside your {areaName}?</h1>
        <p className="onb-sub">
          Tick the patches and flower beds inside your {areaName}. Every plant belongs to one — so you can focus on a single patch at a time.
        </p>

        {/* D-03 — plot plan with current area highlighted + pins */}
        <div style={{ marginTop: 12 }}>
          <OnbPlotPlan areas={derivedPlotAreas} pins={pins} highlightArea={areaName}/>
        </div>

        {/* Sub-space pill list — UNCHANGED behaviour; .onb-pin class for style */}
        <div className="onb-pin-list">
          {chips.map((label, i) => (
            <button
              key={label}
              type="button"
              aria-pressed={ticked.has(label)}
              className={`onb-pin ${ticked.has(label) ? "is-selected" : ""}`}
              onClick={() => toggle(label)}
            >
              <span className="onb-pin-dot" style={{ "--pin-color": PIN_COLORS[i % PIN_COLORS.length] }}/>
              {label}
            </button>
          ))}
        </div>

        {/* Add custom sub-space — UNCHANGED behaviour */}
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <ClearableInput
            type="text"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value.slice(0, 40))}
            placeholder={`Add another spot in your ${areaName.toLowerCase()}…`}
            maxLength={40}
            aria-label={`Add a custom space within ${areaName}`}
            wrapperStyle={{ flex: 1 }}
            style={{ padding: "10px 12px", border: "1px solid var(--c-border)", borderRadius: 10, background: "var(--c-surface)", color: "var(--c-text)", fontSize: "var(--font-body)", minHeight: 44, width: "100%", boxSizing: "border-box" }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          />
          <button
            type="button"
            onClick={addCustom}
            disabled={!customInput.trim()}
            aria-label="Add custom space"
            style={{ background: "var(--c-surface)", border: "1px solid var(--c-border)", borderRadius: 10, padding: "0 16px", minHeight: 44, color: "var(--c-text)", cursor: customInput.trim() ? "pointer" : "not-allowed" }}
          >
            <Plus size={18}/>
          </button>
        </div>

        <div className="push-bottom">
          {/* [Rule 3 - Blocking] Button text kept as exact "Done with <areaName>"
              (no trailing arrow). Test at src/App.test.jsx:1870 asserts
              getByRole('button', { name: /^Done with Back Garden$/i }) —
              trailing " →" would break the ^...$ anchor match. */}
          <button type="button" className="onb-cta" onClick={handleContinue}>
            Done with {areaName}
          </button>
        </div>
      </div>
    </OnbFrame>
  );
}
